// =============================================================================
// src/features/analytics/analytics-insights-block-b-data.ts - Bloco B,
// "Terreno de pauta" (cards 04-06). Proveniencia: Diario_Anexo-
// CardsTaticos_2026-07-25.md linhas 178-317, portado verbatim em 2026-07-27.
// =============================================================================

import type { AnalyticsInsightBlock, AnalyticsInsightCard } from './analytics-insights-types';

const ANALYTICS_INSIGHT_CARD_04: AnalyticsInsightCard = {
  cardNumber: 4,
  cardTitle: 'A pauta de renda já tem dono, e o crédito foi dado em público por um adversário',
  priorityTitle: 'A pauta de renda já tem dono, e o crédito foi dado em público',
  impactScore: 4,
  classificationLabel: 'AMEAÇA DE PAUTA',
  evidenceBlocks: [
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'Na base própria, renda é a segunda demanda espontânea — e o programa que responde a ela já tem nome ' +
        'e autor atribuídos publicamente por outro ator da disputa.',
    },
    {
      blockKind: 'table',
      columnHeaders: ['Demanda espontânea na base própria', 'Comentários'],
      tableRows: [
        ['Segurança / polícia / violência', '<strong>14</strong>'],
        ['<strong>Emprego / renda / auxílio</strong>', '<strong>12</strong>'],
        ['Concurso / aprovados / convocação', '11'],
        ['Interior / município', '7'],
        ['Educação / escola / professor', '1'],
        ['Saúde / hospital / fila', '1'],
        ['Transporte / mobilidade', '<strong>0</strong>'],
        ['Total de comentários na base própria', '440'],
      ],
    },
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'Do outro lado, o Auxílio Estadual aparece creditado a Roberto Cidade na publicação de maior ' +
        'engajamento de Wilson Lima do período (2.731). E nas publicações próprias da janela, o tema ' +
        'economia/renda aparece em <strong>8 publicações</strong>, contra 18 de segurança e 18 de campanha e chapa — ' +
        'embora seja o tema com a segunda melhor taxa de conversão da grade própria.',
    },
    {
      blockKind: 'table',
      columnHeaders: ['Tema das publicações próprias', 'Publicações', 'Engajamento por publicação', 'Comentários por 100 curtidas'],
      tableRows: [
        ['Segurança', '18', '<strong>668</strong>', '37,4'],
        ['Campanha / chapa', '18', '540', '36,5'],
        ['Economia / renda', '8', '432', '<strong>41,0</strong>'],
        ['Interior / caravana', '8', '289', '44,0'],
        ['Obras / infraestrutura', '3', '214', '<strong>57,5</strong>'],
      ],
    },
  ],
  tacticalReadingHtml: [
    'O que o número não entrega: a pauta de renda não está livre e não está em disputa — ela já foi ' +
      'adjudicada, e a adjudicação foi feita por um adversário elogiando outro, o que a torna muito mais ' +
      'difícil de contestar do que uma promessa de campanha. Reivindicar autoria agora coloca o monitorado ' +
      'como terceiro numa conversa que dois atores já fecharam entre si, e o CARD 01 mostra que esses dois ' +
      'são o mesmo bloco. A leitura secundária é de execução: os dois temas de melhor conversão da grade ' +
      'própria — obras e renda — são os dois menos publicados.',
  ],
  actionCallHtml:
    'Não disputar a autoria do Auxílio Estadual: a atribuição pública já ocorreu e contestá-la dá palco ' +
    'a quem já tem o crédito. Ocupar a pauta de renda pelo flanco que está aberto na base própria — ' +
    'concurso e convocação, 11 menções sem nenhuma resposta pública na janela — que é demanda da mesma ' +
    'natureza e ainda não tem dono.',
  confidenceLine:
    'ALTA — validado contra viés de construção do classificador temático: a leitura foi refeita por busca textual ' +
    'independente, com resultado concordante',
  natureLine: 'conjuntural',
  baseLine: '440 comentários na base própria e 61 publicações próprias, 17 a 24/07',
  validityLine: 'revalidar na próxima edição · ação até 08/08/2026',
  seriesLine: '2ª edição · aberto desde 25/07 · deriva instrumental, tese e impacto estáveis',
};

const ANALYTICS_INSIGHT_CARD_05: AnalyticsInsightCard = {
  cardNumber: 5,
  cardTitle: 'A maior pauta da semana ficou sem dono, e a cadeira de acusador segue vaga',
  priorityTitle: 'A maior pauta da semana ficou sem dono e a cadeira de acusador vaga',
  impactScore: 3,
  classificationLabel: 'OPORTUNIDADE PERDIDA',
  evidenceBlocks: [
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'Mobilidade e transporte coletivo foi o assunto de maior volume da janela: <strong>240 matérias e 138 ' +
        'publicações</strong>, com pico de 91 matérias em 23/07.',
    },
    {
      blockKind: 'table',
      columnHeaders: ['Dia', 'Matérias sobre transporte'],
      tableRows: [
        ['18/07', '1'],
        ['20/07', '38'],
        ['21/07', '47'],
        ['22/07', '56'],
        ['<strong>23/07</strong>', '<strong>91</strong>'],
        ['24/07', '7'],
      ],
    },
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'Sobre esse assunto, um único ator da disputa publicou: <strong>David Almeida, com 3 publicações</strong>. Os ' +
        'demais seis campos não publicaram nada. A confirmação por busca textual independente devolve os ' +
        'mesmos três itens, nos dois perfis dele. E na base própria, transporte é a única demanda com <strong>zero</strong> ' +
        'comentários.',
    },
  ],
  tacticalReadingHtml: [
    'A leitura decisiva não é o volume — é quem estava sendo desgastado. Uma paralisação de rodoviários ' +
      'desgasta quem administra, e o único ator que se posicionou foi o que usou o episódio para acusar o ' +
      'governo do estado. A cadeira de acusador ficou vaga para todos os outros, inclusive para o ' +
      'monitorado, e a curva mostra que a janela já fechou: o pico foi em 23/07 e o assunto caiu para 7 ' +
      'matérias no dia seguinte. O card entrega o critério para a próxima, não a ação para esta. O detalhe ' +
      'que qualifica a perda: com zero menção na base própria, o assunto que dominou a imprensa da praça ' +
      'não chegou à audiência própria — o que significa que a ausência custou pouco em público próprio e ' +
      'muito em espaço de imprensa.',
  ],
  actionCallHtml:
    'Não perseguir o assunto agora: o ciclo se encerrou e entrar tarde numa pauta cujo pico passou há ' +
    'dois dias produz reação, não iniciativa. A ação é de critério permanente: definir de antemão quem ' +
    'responde por pauta de serviço urbano em crise, para que a próxima paralisação encontre posição ' +
    'pronta em vez de decisão a tomar.',
  confidenceLine:
    'ALTA — ausência confirmada por dois métodos independentes (classificação temática e busca textual), como exige card de ausência',
  natureLine: 'critério permanente',
  baseLine: '240 matérias e 138 publicações no tema, 17 a 24/07',
  validityLine: 'revalidar em 2 edições · fecha com o ciclo',
  seriesLine: '2ª edição · aberto desde 25/07 · deriva estável',
};

const ANALYTICS_INSIGHT_CARD_06: AnalyticsInsightCard = {
  cardNumber: 6,
  cardTitle: 'A conversa de maior tração da praça é ir embora do estado',
  priorityTitle: 'A conversa de maior tração da praça é ir embora do estado',
  impactScore: 4,
  classificationLabel: 'OPORTUNIDADE',
  evidenceBlocks: [
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'Ordenando todos os comentários da janela por curtida, sem filtro de assunto, o topo inteiro está ' +
        'fora da política — e a publicação que o gerou trata de deixar o Amazonas.',
    },
    {
      blockKind: 'table',
      columnHeaders: ['Publicação', 'Alcance', 'Comentários declarados', 'Curtidas nos comentários'],
      tableRows: [
        ['<strong>Página de cidade — amazonense que se mudou para Santa Catarina</strong>', '184.259', '1.882', '<strong>1.712</strong>'],
        ['Página de cidade — segurança da mulher em Manaus', '52.525', '974', '634'],
        ['Página de trânsito — alerta a condutores', '48.796', '402', '394'],
        ['<strong>Melhor publicação política da janela</strong>', '69.725', '760', '<strong>291</strong>'],
      ],
    },
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'A conversa não-política gera <strong>5,9 vezes</strong> mais curtidas em comentário que a melhor publicação ' +
        'política do período. O comentário mais curtido de toda a janela — 414 curtidas — está nessa ' +
        'publicação.',
    },
    {
      blockKind: 'quote',
      quoteHtml:
        '"Não entendo porque o amazonense sempre trata com desdém para quem sai de Manaus, ela não mentiu em nada!"',
      quoteAttribution: 'comentário mais curtido da janela, 414 curtidas',
    },
  ],
  tacticalReadingHtml: [
    'Existe uma pauta emocional com audiência formada, sem dono político e de tração muito superior a ' +
      'tudo que a disputa produziu: permanecer ou sair do estado. Ela não aparece em nenhum ranking ' +
      'político porque não é sobre candidato — e é justamente por isso que tem tração. O que o número não ' +
      'entrega: essa conversa é a forma vernacular de um tema clássico de campanha — emprego, custo de ' +
      'vida, oportunidade — mas quem falar dela em linguagem de programa de governo não entra. O CARD 03 ' +
      'mostra que essas mesmas páginas concentram o público não-alinhado; os dois cards descrevem o mesmo ' +
      'espaço por caminhos diferentes.',
  ],
  actionCallHtml:
    'Adotar permanência no estado como eixo de discurso, na linguagem em que a praça já fala dela — ' +
    'quem fica, o que falta para ficar, o que faz voltar. Testar em duas semanas nas páginas do CARD 03, ' +
    'antes de levar para a grade própria.',
  confidenceLine:
    'ALTA — validado contra artefato de plataforma: curtida em comentário só funciona em uma das redes, e a ' +
    'comparação foi feita inteiramente dentro dela',
  natureLine: 'estrutural',
  baseLine: '1.578 comentários na rede em que a métrica existe, dos quais 1.048 com ao menos uma curtida, 17 a 24/07',
  validityLine: 'revalidar na próxima edição · ação até 08/08/2026',
  seriesLine: '2ª edição · aberto desde 25/07 · deriva estável',
};

export const ANALYTICS_INSIGHTS_BLOCK_B: AnalyticsInsightBlock = {
  blockId: 'B',
  blockTitle: 'Bloco B — Terreno de pauta',
  blockCards: [ANALYTICS_INSIGHT_CARD_04, ANALYTICS_INSIGHT_CARD_05, ANALYTICS_INSIGHT_CARD_06],
};
